/**
 * Faster, flicker-free colour switching on product pages.
 * 1. Caches Horizon's variant re-render responses, so returning to a colour is instant.
 * 2. Preloads a colour's photos (same width the gallery uses) as soon as the shopper hovers/touches that
 *    colour, and the first photo of every colour when the browser is idle.
 * 3. Keeps the previous photo on screen until the new one has decoded (no white flash).
 * Photos are grouped by colour the same way as the gallery (file-name prefix, else position).
 */
const handle = location.pathname.split('/products/')[1]?.split(/[/?#]/)[0];

// ---- 1. Cache variant re-renders ----
if (handle && !window.__anjaFetchCache) {
  window.__anjaFetchCache = new Map();
  const original = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    if (method === 'GET' && url.includes(`/products/${handle}`) && url.includes('section_id=') && /option_values=|variant=/.test(url)) {
      const hit = window.__anjaFetchCache.get(url);
      if (hit) return new Response(hit.body, { status: 200, headers: hit.headers });
      const res = await original(input, init);
      if (res.ok) {
        const body = await res.clone().text();
        window.__anjaFetchCache.set(url, { body, headers: { 'Content-Type': res.headers.get('Content-Type') || 'text/html' } });
      }
      return res;
    }
    return original(input, init);
  };
}

// ---- 2. Preload colour photos ----
/** @type {Map<string, string[]>} colour (lower case) → photo URLs */
const groups = new Map();
const preloaded = new Set();

/** @param {string} src */
const key = (src) => {
  const file = (src.split('?')[0].split('/').pop() || '').split('.')[0].toLowerCase();
  const parts = file.split('-');
  return /^\d+$/.test(parts[parts.length - 1]) && parts.length > 1 ? parts.slice(0, -1).join('-') : file;
};

const galleryWidth = () => {
  const img = /** @type {HTMLImageElement | null} */ (document.querySelector('.product-media-container img'));
  const m = img?.currentSrc?.match(/[?&]width=(\d+)/);
  return m ? Number(m[1]) : Math.min(1500, Math.round((img?.clientWidth || 600) * (devicePixelRatio || 1)));
};

/** @param {string} src @param {number} width */
const sized = (src, width) => {
  const u = new URL(src, location.origin);
  u.searchParams.set('width', String(width));
  return u.toString();
};

/** @param {string} colour @param {boolean} [all] */
function preload(colour, all = true) {
  const list = groups.get(colour.toLowerCase());
  if (!list) return;
  const width = galleryWidth();
  for (const src of all ? list : list.slice(0, 1)) {
    const url = sized(src, width);
    if (preloaded.has(url)) continue;
    preloaded.add(url);
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
  }
}

async function build() {
  if (!handle) return;
  try {
    const root = window.Shopify?.routes?.root || '/';
    const p = await (await fetch(`${root}products/${handle}.js`)).json();
    const ci = p.options.findIndex((o) => /colou?r|لون/i.test(o.name || o));
    if (ci < 0) return;
    const media = p.media.filter((m) => m.media_type === 'image').map((m) => ({ id: m.id, src: m.src || m.preview_image?.src }));
    const anchors = new Map(); // colour → media id
    p.variants.forEach((v) => {
      const c = v.options[ci];
      if (v.featured_media && !anchors.has(c)) anchors.set(c, v.featured_media.id);
    });
    const keys = media.map((m) => key(m.src));
    const grouped = new Set(keys).size < media.length;
    const anchorKeys = new Map([...anchors].map(([c, id]) => [c, keys[media.findIndex((m) => m.id === id)]]));
    const anchorIdx = [...anchors.values()].map((id) => media.findIndex((m) => m.id === id)).sort((a, b) => a - b);
    anchors.forEach((id, colour) => {
      let list;
      if (grouped) list = media.filter((m, i) => keys[i] === anchorKeys.get(colour));
      else {
        const start = media.findIndex((m) => m.id === id);
        const next = anchorIdx.find((i) => i > start) ?? media.length;
        list = media.slice(start, next);
      }
      const first = media.find((m) => m.id === id);
      groups.set(colour.toLowerCase(), [first.src, ...list.filter((m) => m.id !== id).map((m) => m.src)]);
    });
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
    idle(() => groups.forEach((_, colour) => preload(colour, false)));
  } catch {
    /* preloading is best effort */
  }
}

const onIntent = (event) => {
  const label = /** @type {HTMLElement} */ (event.target).closest?.('variant-picker label');
  if (!label) return;
  const input = /** @type {HTMLInputElement | null} */ (label.querySelector('input') || document.getElementById(/** @type {HTMLLabelElement} */ (label).htmlFor));
  if (input?.value) preload(input.value);
};
document.addEventListener('pointerover', onIntent, { passive: true });
document.addEventListener('touchstart', onIntent, { passive: true });
document.addEventListener('focusin', onIntent);

// ---- 3. No white flash: fade new photos in once decoded ----
new MutationObserver((records) => {
  for (const r of records) {
    r.addedNodes.forEach((n) => {
      if (!(n instanceof HTMLElement)) return;
      n.querySelectorAll?.('.product-media-container img').forEach((img) => {
        const el = /** @type {HTMLImageElement} */ (img);
        if (el.complete) return;
        el.classList.add('anja-img-loading');
        const done = () => el.classList.remove('anja-img-loading');
        el.addEventListener('load', done, { once: true });
        el.addEventListener('error', done, { once: true });
      });
    });
  }
}).observe(document.body, { childList: true, subtree: true });

build();
